import { Icon } from './icons';
export { Icon } from './icons';
export { Select } from './dropdown';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from '@tanstack/react-router';
import { statusLabel } from './i18n';
import { field, name, type Row } from './api/client';

export function Avatar({ label, size = 32 }: { label: string; size?: number }) {
  const initials = label
    .trim()
    .split(/\s+/)
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: size > 48 ? 20 : size > 32 ? 15 : 12 }}
    >
      {initials || <Icon name="Profile" size={Math.max(16, size / 2)} />}
    </span>
  );
}
export function Button({
  children,
  icon,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: string }) {
  return (
    <button type="button" className={`pill ${className}`} {...props}>
      {icon && <Icon name={icon} size={16} />}
      {children}
    </button>
  );
}
export function IconButton({
  icon,
  label,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { icon: string; label: string }) {
  return (
    <button type="button" className="icon-button" aria-label={label} {...props}>
      <Icon name={icon} />
    </button>
  );
}
export function Empty({
  icon,
  title,
  subtitle,
  children,
}: {
  icon?: string;
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <Icon name={icon} size={104} />}
      <div className="empty-title">{title}</div>
      {subtitle && <div className="empty-subtitle">{subtitle}</div>}
      {children}
    </div>
  );
}
export function ErrorState({ error, retry }: { error: Error | null; retry?: () => void }) {
  return (
    <div className="error-state" role="alert">
      <Icon name="InfoOutline" />
      <span className="error-message">{error?.message || 'Не удалось загрузить страницу'}</span>
      {retry && <Button onClick={retry}>Попробовать снова</Button>}
    </div>
  );
}
export function Loading() {
  return (
    <div className="loading" role="status" aria-label="Загрузка">
      <span />
      <span />
      <span />
    </div>
  );
}
export function Status({ value, label }: { value: string; label?: string }) {
  const formatted = label || statusLabel(value);
  return <span className={`status status-${value.toLowerCase()}`}>{formatted}</span>;
}
export function StatTabs({
  tabs,
  selected,
  onChange,
}: {
  tabs: { label: string; value: string; count?: number | string }[];
  selected: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="stat-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={selected === tab.value}
          className={selected === tab.value ? 'selected' : ''}
          onClick={() => onChange(tab.value)}
        >
          {tab.count !== undefined && <span className="stat-count">{tab.count}</span>}
          <span>{tab.label}</span>
        </button>
      ))}
    </div>
  );
}
export function Tabs({
  tabs,
  value,
  onChange,
}: {
  tabs: { label: string; value: string; icon?: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          role="tab"
          aria-selected={value === tab.value}
          type="button"
          key={tab.value}
          className={value === tab.value ? 'selected' : ''}
          onClick={() => onChange(tab.value)}
        >
          {tab.icon && <Icon name={tab.icon} size={16} />}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Search({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [expanded, setExpanded] = useState(Boolean(value));
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (expanded) ref.current?.focus();
  }, [expanded]);
  return (
    <div className={`search ${expanded ? 'expanded' : ''}`}>
      <button type="button" aria-label={placeholder} onClick={() => setExpanded(true)}>
        <Icon name="Search" />
      </button>
      <input
        ref={ref}
        type="search"
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          if (!value) setExpanded(false);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !value) setExpanded(false);
        }}
      />
    </div>
  );
}

export function Overlay({
  title,
  children,
  onClose,
  variant = 'drawer',
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  variant?: 'drawer' | 'dialog';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const close = useCallback(() => {
    if (closeTimer.current !== null) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      onClose();
      return;
    }
    setClosing(true);
    closeTimer.current = setTimeout(onClose, 150);
  }, [onClose]);
  useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    },
    [],
  );
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const controls = ref.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled),input:not([aria-hidden="true"]):not([type="hidden"]),select:not([hidden]),textarea,a[href],[tabindex="0"]',
      );
      if (!controls?.length) return;
      const first = controls[0],
        last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      previous?.focus();
    };
  }, [close]);
  return (
    <div
      className={`overlay-backdrop ${variant} ${closing ? 'is-closing' : ''}`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="overlay"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <header>
          <h2>{title}</h2>
          <IconButton icon="Cross" label="Закрыть" onClick={close} />
        </header>
        {children}
      </div>
    </div>
  );
}

export const ToastContext = createContext<(message: string) => void>(() => {});
export function useToast() {
  return useContext(ToastContext);
}

export type Column<T> = {
  key: string;
  label: string;
  width?: number;
  render?: (row: T) => ReactNode;
  filter?: boolean;
  sortable?: boolean;
};
export function Table<T extends Row>({
  rows,
  columns,
  empty = 'Здесь пока ничего нет',
  onRow,
  loading = false,
  hasMore = false,
  loadMore,
  onSort,
}: {
  rows: T[];
  columns: Column<T>[];
  empty?: string;
  onRow?: (row: T) => void;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
}) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const visible = rows.filter((row) =>
    Object.entries(selected).every(
      ([key, values]) => !values.length || values.includes(field(row, key)),
    ),
  );
  if (sort && !onSort)
    visible.sort(
      (a, b) =>
        field(a, sort.key).localeCompare(field(b, sort.key), undefined, { numeric: true }) *
        (sort.direction === 'asc' ? 1 : -1),
    );
  const values = filter
    ? [...new Set(rows.map((row) => field(row, filter)).filter(Boolean))].filter((value) =>
        value.toLowerCase().includes(search.toLowerCase()),
      )
    : [];
  const firstLabelColumn = columns.findIndex((column) => Boolean(column.label));
  return (
    <>
      <div className="table-scroll">
        <table>
          <colgroup>
            {columns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={column.key} className={sort?.key === column.key ? 'sorted' : ''}>
                  <span>
                    {column.label}
                    {index === firstLabelColumn && rows.length > 0 && (
                      <span className="muted">
                        {' '}
                        · {rows.length}
                        {hasMore ? '+' : ''}
                      </span>
                    )}
                  </span>
                  <span className="column-controls">
                    {column.sortable !== false && (
                      <button
                        type="button"
                        aria-label={`Сортировка: ${column.label}`}
                        onClick={() => {
                          const direction =
                            sort?.key === column.key && sort.direction === 'asc' ? 'desc' : 'asc';
                          setSort({ key: column.key, direction });
                          onSort?.(column.key, direction);
                        }}
                      >
                        <Icon
                          name={
                            sort?.key === column.key && sort.direction === 'asc'
                              ? 'ArrowUp'
                              : 'ArrowDown'
                          }
                          size={16}
                        />
                      </button>
                    )}
                    {column.filter !== false && (
                      <button
                        type="button"
                        aria-label={`Фильтр: ${column.label}`}
                        aria-expanded={filter === column.key}
                        className={selected[column.key]?.length ? 'active' : ''}
                        onClick={() => {
                          setFilter(column.key);
                          setDraft(selected[column.key] || []);
                          setSearch('');
                        }}
                      >
                        <Icon name="Filter" size={16} />
                      </button>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id}>
                {columns.map((column, index) => (
                  <td
                    key={column.key}
                    onClick={() => {
                      if (onRow) onRow(row);
                    }}
                  >
                    <span className={index === 0 && onRow ? 'primary-cell' : ''}>
                      {column.render ? column.render(row) : field(row, column.key, '-')}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {loading ? <Loading /> : !visible.length && <div className="table-empty">{empty}</div>}
      </div>
      {hasMore && (
        <div className="load-more">
          <Button onClick={loadMore}>Загрузить ещё</Button>
        </div>
      )}
      {filter && (
        <Overlay
          title={`Фильтр: ${columns.find((column) => column.key === filter)?.label}`}
          variant="dialog"
          onClose={() => setFilter(null)}
        >
          <div className="filter-search">
            <input
              type="search"
              placeholder="Поиск"
              aria-label="Поиск значений"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Icon name="Search" />
          </div>
          <div className="filter-options">
            <label>
              <input
                type="checkbox"
                checked={Boolean(values.length && values.every((value) => draft.includes(value)))}
                onChange={(event) => setDraft(event.target.checked ? values : [])}
              />
              Выбрать все
            </label>
            {values.map((value) => (
              <label key={value}>
                <input
                  type="checkbox"
                  checked={draft.includes(value)}
                  onChange={(event) =>
                    setDraft(
                      event.target.checked
                        ? [...draft, value]
                        : draft.filter((item) => item !== value),
                    )
                  }
                />
                {value}
              </label>
            ))}
          </div>
          <footer>
            <Button
              onClick={() => {
                setSelected((previous) => ({ ...previous, [filter]: [] }));
                setFilter(null);
              }}
            >
              Сбросить
            </Button>
            <Button
              onClick={() => {
                setSelected((previous) => ({ ...previous, [filter]: draft }));
                setFilter(null);
              }}
            >
              Применить
            </Button>
          </footer>
        </Overlay>
      )}
    </>
  );
}

export function PersonCell({ person }: { person: Row | undefined }) {
  if (!person) return <span className="muted">-</span>;
  return (
    <Link
      to="/employees/profile/$employeeId"
      params={{ employeeId: person.id }}
      className="person-cell"
    >
      <Avatar label={name(person)} />
      {name(person)}
    </Link>
  );
}

export function Field({
  label,
  children,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; children?: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children || <input {...props} />}
    </label>
  );
}
export function FilePicker({
  label,
  description,
  onChange,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string; description: string }) {
  const [names, setNames] = useState<string[]>([]);
  return (
    <div className="file-drop">
      <div className="file-drop-title">
        <Icon name="Clip" size={20} />
        <span>{names.length ? names.join(', ') : label}</span>
      </div>
      <small className="muted">{description}</small>
      <input
        {...props}
        type="file"
        aria-label={label}
        onChange={(event) => {
          setNames(Array.from(event.target.files || [], (file) => file.name));
          onChange?.(event);
        }}
      />
    </div>
  );
}
